import {
	closestCenter,
	DndContext,
	type DragEndEvent,
	DragOverlay,
	type DragStartEvent,
	MouseSensor,
	TouchSensor,
	useSensor,
	useSensors
} from '@dnd-kit/core'
import {
	arrayMove,
	rectSortingStrategy,
	SortableContext
} from '@dnd-kit/sortable'
import { useEffect, useId, useMemo, useState } from 'react'
import { Checkbox } from '@/components/commons/checkbox'
import { Chip } from '@/components/commons/chip'
import { GalleryImageItem } from '@/components/commons/gallery-image-item'
import { GalleryModal } from '@/components/commons/gallery-modal'
import { GalleryModalHeader } from '@/components/commons/gallery-modal-header'
import { GalleryModalThumbnails } from '@/components/commons/gallery-modal-thumbnails'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { AlertTriangleIcon, CheckIcon, MoveIcon } from '@/components/ui/icons'
import { Separator } from '@/components/ui/separator'
import type { ExperienceGalleryImage } from '@/features/experience-shell/types'
import type { RemapExperienceVetting } from '@/features/experience-vetting/types.ts'
import {
	OpsVettingDisabledContainer,
	OpsVettingGalleryGrid,
	OpsVettingParagraph,
	OpsVettingSectionTitle,
	OpsVettingSectionWrapper
} from '@/features/ops-vetting/components/ops-vetting-atoms'
import { useGalleryNeedsReviewShortcuts } from '@/features/photoset/hooks/use-gallery-needs-review-shortcuts'
import { useExperienceVettingPhotoUpload } from '@/features/service-vetting/store/services-photo-upload.ts'
import { useExperienceVettingError } from '@/features/service-vetting/store/use-service-vetting-error'
import { useTranslation } from '@/translations/i18n.tsx'
import { useGetExperiencesFlags } from '../http/use-get-experiences-flags'
import { useAddExperienceGalleryFlags } from '../queries/use-add-experience-gallery-flags'
import { useRemoveExperienceGalleryFlags } from '../queries/use-remove-experience-gallery-flags'
import { useUpdateExperienceVettingGalleryImagesPositions } from '../queries/use-update-experience-vetting-gallery-images-positions'
import { ExpVettingCriteriaGalleryUploadedItem } from './exp-vetting-criteria-gallery-uploaded-item'
import { ExpVettingCriteriaGalleryUploadingItem } from './exp-vetting-criteria-gallery-uploading-item'
import { ExperienceVettingCriteriaCoverPhoto } from './experience-vetting-criteria-cover-photo'
import { ExperienceVettingCriteriaGalleryItem } from './experience-vetting-criteria-gallery-item'
import { ExperienceVettingCriteriaUploadButton } from './experience-vetting-criteria-upload-button'

const StatusMessage = ({
	pendingImagesCount
}: {
	pendingImagesCount: number
}) => {
	const { t } = useTranslation()
	if (pendingImagesCount > 0) {
		return t('vetting:criteria.commons.gallery.images_remaining', {
			value: pendingImagesCount
		})
	}

	return (
		<span className='flex gap-1'>
			<CheckIcon className='fill-success-600' size={20} />
			{t('vetting:criteria.commons.gallery.images_reviewed')}
		</span>
	)
}

type FlagKeyName = 'lack_photo_variety' | 'requires_pro_photography'

export const ExperienceVettingCriteriaGallery = ({
	experience,
	isReadOnly
}: {
	experience: RemapExperienceVetting
	isReadOnly: boolean
}) => {
	const { t } = useTranslation()
	const { gallery } = experience
	const lackPhotoVarietyId = useId()
	const requiresProPhotographyId = useId()
	const isDeclined = experience.expertise.status === 'declined'
	const isDisabled = isReadOnly || isDeclined
	const [activeId, setActiveId] = useState<number | string | null>(null)
	const [orderedImages, setOrderedImages] = useState(gallery.images)

	useEffect(() => {
		setOrderedImages(gallery.images)
	}, [gallery.images])

	const flagsQuery = useGetExperiencesFlags({
		scopes: ['gallery']
	})

	const addGalleryFlags = useAddExperienceGalleryFlags({
		experienceId: experience.id,
		itemId: experience.gallery.id
	})
	const removeGalleryFlags = useRemoveExperienceGalleryFlags({
		experienceId: experience.id,
		itemId: experience.gallery.id
	})
	const updateExperienceVettingGalleryImagesPositions =
		useUpdateExperienceVettingGalleryImagesPositions({
			experienceId: experience.id,
			galleryId: experience.gallery.id
		})

	const [expandedImageIndex, setExpandedImageIndex] = useState<number | null>(
		null
	)
	const [showGalleryModal, setShowGalleryModal] = useState(false)

	const errors = useExperienceVettingError((state) => state.errors)
	const { galleryItems: uploadingItems } = useExperienceVettingPhotoUpload()

	const coverPhoto = useMemo(
		() => orderedImages.find((image) => image.isCover),
		[orderedImages]
	)

	const filteredGalleryImages = useMemo(
		() =>
			orderedImages.filter(
				(image) =>
					!(image.isCover || image.isItineraryImage) &&
					image.isApproved !== false
			),
		[orderedImages]
	)

	const sortedGalleryImages = useMemo(() => {
		const filteredImages = orderedImages.filter(
			(image) =>
				image.isCover ||
				(!image.isItineraryImage && image.isApproved !== false)
		)

		// Sort the images according to the specified priority
		return filteredImages.sort((a, b) => {
			// Cover images come first
			if (a.isCover && !b.isCover) {
				return -1
			}
			if (!a.isCover && b.isCover) {
				return 1
			}

			// If all criteria are the same, maintain original order
			return 0
		})
	}, [orderedImages])

	const pendingImagesCount = useMemo(
		() =>
			filteredGalleryImages.filter(
				(image) =>
					image.isApproved === null && !image.isHostApprovalNeeded
			).length,
		[filteredGalleryImages]
	)

	const handleFlagChange = (flagKeyName: FlagKeyName, isChecked: boolean) => {
		const flag = flagsQuery.data?.find(
			(flag) => flag.keyName === flagKeyName
		)
		if (!flag) {
			return
		}

		const mutationFunction = isChecked
			? addGalleryFlags
			: removeGalleryFlags
		mutationFunction.mutate({ flags: [flag] })
	}

	const handleClickExpandedImage = (
		imageId: ExperienceGalleryImage['id']
	) => {
		const index = sortedGalleryImages.findIndex(
			(image) => image.id === imageId
		)
		setExpandedImageIndex(index)
		setShowGalleryModal(true)
	}

	const handleCloseModal = () => {
		setExpandedImageIndex(null)
		setShowGalleryModal(false)
	}

	const isFlagChecked = (flagKeyName: FlagKeyName) =>
		gallery.flags.some((flag) => flag.keyName === flagKeyName)

	const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor))
	const handleDragStart = (event: DragStartEvent) => {
		setActiveId(event.active.id)
	}
	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event

		if (!over) {
			setActiveId(null)
			return
		}

		const oldIndex = orderedImages.findIndex(
			(image) => image.id === active.id
		)
		const newIndex = orderedImages.findIndex(
			(image) => image.id === over.id
		)

		if (oldIndex !== newIndex) {
			// Update local state immediately for responsive UI
			const newOrderedImages = arrayMove(
				[...orderedImages],
				oldIndex,
				newIndex
			)
			setOrderedImages(newOrderedImages)

			updateExperienceVettingGalleryImagesPositions.mutate(
				newOrderedImages.map((image, index) => ({
					id: image.id,
					position: index
				}))
			)
		}

		setActiveId(null)
	}
	const handleDragCancel = () => {
		setActiveId(null)
	}

	useGalleryNeedsReviewShortcuts({
		selectableImages: sortedGalleryImages,
		currentDetailImageIndex: expandedImageIndex || 0,
		setCurrentDetailImageIndex: setExpandedImageIndex,
		onClose: handleCloseModal
	})

	return (
		<OpsVettingSectionWrapper direction='column' fullWidth gap={6}>
			<div className='flex w-full items-center justify-between'>
				<div className='flex items-center gap-8'>
					<OpsVettingSectionTitle>
						{t('vetting:criteria.service.gallery.title')}
					</OpsVettingSectionTitle>

					<ExperienceVettingCriteriaUploadButton
						disabled={isDisabled}
						experienceId={experience.id}
						galleryCounter={filteredGalleryImages.length}
						galleryId={experience.gallery.id}
					/>

					<Checkbox
						checked={isFlagChecked('lack_photo_variety')}
						disabled={isDisabled}
						id={lackPhotoVarietyId}
						label={t(
							'vetting:criteria.experience.flags_key_name.lack_photo_variety'
						)}
						onChange={(e) =>
							handleFlagChange(
								'lack_photo_variety',
								e.currentTarget.checked
							)
						}
					/>

					<Checkbox
						checked={isFlagChecked('requires_pro_photography')}
						disabled={isDisabled}
						id={requiresProPhotographyId}
						label={t(
							'vetting:criteria.commons.requires_pro_photography'
						)}
						onChange={(e) =>
							handleFlagChange(
								'requires_pro_photography',
								e.currentTarget.checked
							)
						}
					/>
				</div>

				<div className='flex items-center gap-4'>
					{errors.galleryErrors.map((error) => (
						<Chip
							isActive
							key={error}
							label={t(error)}
							startSlot={<AlertTriangleIcon />}
							variant='danger'
						/>
					))}

					<OpsVettingParagraph>
						<StatusMessage
							pendingImagesCount={pendingImagesCount}
						/>
					</OpsVettingParagraph>
				</div>
			</div>

			<Separator orientation='horizontal' />

			<OpsVettingDisabledContainer $isDisabled={isDeclined}>
				<OpsVettingGalleryGrid>
					<ExperienceVettingCriteriaCoverPhoto
						coverPhoto={coverPhoto}
						experienceId={experience.id}
						galleryId={gallery.id}
						hideAction={isReadOnly}
						onClickExpand={() => {
							if (coverPhoto) {
								handleClickExpandedImage(coverPhoto.id)
							}
						}}
					/>

					<DndContext
						collisionDetection={closestCenter}
						onDragCancel={handleDragCancel}
						onDragEnd={handleDragEnd}
						onDragStart={handleDragStart}
						sensors={sensors}
					>
						<SortableContext
							items={filteredGalleryImages}
							strategy={rectSortingStrategy}
						>
							{filteredGalleryImages.map((image) => {
								return image.isHostApprovalNeeded ? (
									<ExpVettingCriteriaGalleryUploadedItem
										experienceId={experience.id}
										galleryId={gallery.id}
										hideAction={isReadOnly}
										image={image}
										key={image.id}
										onClickExpand={() =>
											handleClickExpandedImage(image.id)
										}
									/>
								) : (
									<ExperienceVettingCriteriaGalleryItem
										experienceId={experience.id}
										galleryId={gallery.id}
										hideAction={isReadOnly}
										image={image}
										key={image.id}
										onClickExpand={() =>
											handleClickExpandedImage(image.id)
										}
									/>
								)
							})}
						</SortableContext>
						<DragOverlay style={{ transformOrigin: '0 0' }}>
							{activeId ? (
								<GalleryImageItem
									src={
										filteredGalleryImages.find(
											(image) => image.id === activeId
										)?.thumbnails?.xs ||
										filteredGalleryImages.find(
											(image) => image.id === activeId
										)?.url ||
										''
									}
									style={{
										cursor: 'grabbing'
									}}
									topLeftSlot={
										<ButtonLegacy
											className='show-on-hover'
											shape='square'
											size='sm'
											type='button'
											variant='secondary'
										>
											<MoveIcon size={16} />
										</ButtonLegacy>
									}
								/>
							) : null}
						</DragOverlay>
					</DndContext>

					{uploadingItems.map((image) => (
						<ExpVettingCriteriaGalleryUploadingItem
							hideAction={isReadOnly}
							image={image}
							key={image.id}
						/>
					))}
				</OpsVettingGalleryGrid>
			</OpsVettingDisabledContainer>

			{showGalleryModal && expandedImageIndex !== null && (
				<GalleryModal
					currentImage={sortedGalleryImages[expandedImageIndex]}
					headerSlot={
						<GalleryModalHeader
							currentImage={
								sortedGalleryImages[expandedImageIndex]
							}
							imagesCount={sortedGalleryImages.length}
							onClose={handleCloseModal}
						/>
					}
					onClose={handleCloseModal}
					thumbnails={{
						l: sortedGalleryImages[expandedImageIndex].url || '',
						m: '',
						s: '',
						xs: ''
					}}
					thumbnailsSlot={
						<GalleryModalThumbnails
							currentImageId={
								sortedGalleryImages[expandedImageIndex].id
							}
							images={sortedGalleryImages.map((image) => ({
								id: image.id,
								name: image.name,
								thumbnails: {
									l: image.url || '',
									m: image.url || '',
									s: image.url || '',
									xs: image.url || ''
								}
							}))}
							onSelect={(_, index) => {
								setExpandedImageIndex(index)
							}}
							thumbnails='thumbnails'
						/>
					}
				/>
			)}
		</OpsVettingSectionWrapper>
	)
}
